import { CashOnDeliveryBadge } from '@/components/orders/CashOnDeliveryBadge';
import { PrintStatusBadge } from '@/components/orders/PrintStatusBadge';
import { OrderStatusBadge } from '@/components/sale/OrderStatusBadge';
import { fDateTime } from '@/utils/datetime-util';
import { Calendar, ChevronRight, Package, User2Icon } from 'lucide-react';
import { memo } from 'react';

interface MobileOrderCardProps {
  row: any;
  onClick: () => void;
  isHighlighted?: boolean;
}

export const MobileOrderCard = memo(({
  row,
  onClick,
  isHighlighted,
}: MobileOrderCardProps) => {
  // Memoize computed values to avoid recalculation
  const orderNumber = row.orderNumber || '';
  const totalAmount = parseFloat(row.totalAmount || 0).toFixed(2);
  const itemCount = row.totalItems || 0;
  const hasFollowUp = !!row.followUpUser;
  const hasPrintUser = !!row.lastPrintUser;

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-lg border transition-all duration-150 active:scale-[0.99]
        ${isHighlighted
          ? 'border-blue-500 bg-blue-50 shadow-sm'
          : 'border-gray-200 hover:border-gray-300'
        }
        p-3 cursor-pointer touch-manipulation
      `}
    >
      {/* Top Row: Order Number, Status, Date, and Total */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="font-mono font-semibold text-xs text-gray-900">
              #{orderNumber}
            </span>
            <div className="scale-[0.85] origin-left">
              <OrderStatusBadge status={row.status} cod={row.cashOnDelivery} />
            </div>
            {row.cashOnDelivery && (
              <CashOnDeliveryBadge isCashOnDelivery={true} />
            )}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-gray-500">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{fDateTime(row.updatedAt)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
          <span className="font-bold text-sm text-indigo-700">
            ${totalAmount}
          </span>
          <div className="flex items-center gap-0.5 text-[10px] text-gray-500">
            <Package className="w-2.5 h-2.5" />
            <span>{itemCount}</span>
          </div>
        </div>
      </div>

      {/* Customer Name Row */}
      <div className="flex items-center gap-1.5 mb-2">
        <User2Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="font-medium text-xs text-gray-900 truncate flex-1">
          {row.customerName || 'N/A'}
        </span>
        <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
      </div>

      {/* Bottom Row: Print Status and Follow-up (if exists) */}
      {(hasPrintUser || hasFollowUp) && (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          {hasPrintUser && (
            <div className="flex-shrink-0">
              <PrintStatusBadge
                username={row.lastPrintUser}
                printDate={row.lastPrintTimestamp}
              />
            </div>
          )}
          {hasFollowUp && (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <User2Icon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="text-[10px] font-medium text-slate-600 truncate">
                {row.followUpUser}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

MobileOrderCard.displayName = 'MobileOrderCard';
